/* eslint-disable no-param-reassign */

import { createAsyncThunk, createSlice, SerializedError } from '@reduxjs/toolkit';
import {
  GetApplicationUnionListResponse,
  getApplicationUnionList,
  GetApplicationEducationIndustryListResponse,
  getApplicationEducationIndustryList,
  GetApplicationEducationOtherListResponse,
  getApplicationEducationOtherList,
  GetApplicationJobListResponse,
  getApplicationJobList,
} from '../api/application';
import { applicationApi } from '../api';
import { RootState } from './rootReducer';
import { ApplicationIndustry, ApplicationSchool } from '../api/openapi';

interface State {
  industries: ApplicationIndustry[] | null;
  industriesLoading: 'idle' | 'pending';
  industriesError: null | SerializedError;

  unions: GetApplicationUnionListResponse[] | null;
  unionsLoading: 'idle' | 'pending';
  unionsError: null | SerializedError;

  schools: ApplicationSchool[] | null;
  schoolsLoading: 'idle' | 'pending';
  schoolsError: null | SerializedError;

  educationIndustries: GetApplicationEducationIndustryListResponse[] | null;
  educationIndustriesLoading: 'idle' | 'pending';
  educationIndustriesError: null | SerializedError;

  educationOthers: GetApplicationEducationOtherListResponse[] | null;
  educationOthersLoading: 'idle' | 'pending';
  educationOthersError: null | SerializedError;

  jobs: GetApplicationJobListResponse[] | null;
  jobsLoading: 'idle' | 'pending';
  jobsError: null | SerializedError;
}

export const fetchIndustries = createAsyncThunk<
  ApplicationIndustry[],
  void,
  {
    state: RootState;
  }
>('createApplication/fetchIndustries', async () => {
  const response = await applicationApi.applicationIndustryList();
  return response.data;
});

export const fetchUnions = createAsyncThunk<
  GetApplicationUnionListResponse[],
  void,
  {
    state: RootState;
  }
>('createApplication/fetchUnions', async () => {
  const response = await getApplicationUnionList();
  return response.data;
});

export const fetchSchools = createAsyncThunk<
  ApplicationSchool[],
  string,
  {
    state: RootState;
  }
>('createApplication/fetchSchools', async (industry) => {
  const response = await applicationApi.applicationSchoolList({
    industry,
  });

  return response.data;
});

export const fetchEducationIndustries = createAsyncThunk<
  GetApplicationEducationIndustryListResponse[],
  void,
  {
    state: RootState;
  }
>('createApplicationEducationIndustries/fetchApplicationEducationIndustries', async () => {
  const response = await getApplicationEducationIndustryList();
  return response.data;
});

export const fetchEducationOthers = createAsyncThunk<
  GetApplicationEducationOtherListResponse[],
  void,
  {
    state: RootState;
  }
>('createApplicationEducationOthers/fetchApplicationEducationOThers', async () => {
  const response = await getApplicationEducationOtherList();
  return response.data;
});

export const fetchJobs = createAsyncThunk<
  GetApplicationJobListResponse[],
  void,
  {
    state: RootState;
  }
>('createApplicationJob/fetchApplicationJob', async () => {
  const response = await getApplicationJobList();
  return response.data;
});

const initialState: State = {
  industries: null,
  industriesLoading: 'idle',
  industriesError: null,

  unions: null,
  unionsLoading: 'idle',
  unionsError: null,

  schools: null,
  schoolsLoading: 'idle',
  schoolsError: null,

  educationIndustries: null,
  educationIndustriesLoading: 'idle',
  educationIndustriesError: null,

  educationOthers: null,
  educationOthersLoading: 'idle',
  educationOthersError: null,

  jobs: null,
  jobsLoading: 'idle',
  jobsError: null,
};

const slice = createSlice({
  name: 'createApplication',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    /*
     * fetchIndustries
     */
    builder.addCase(fetchIndustries.pending, (state) => {
      state.industries = null;
      state.industriesError = null;
      state.industriesLoading = 'pending';
    });
    builder.addCase(fetchIndustries.fulfilled, (state, { payload }) => {
      state.industries = payload;
      state.industriesLoading = 'idle';
    });
    builder.addCase(fetchIndustries.rejected, (state, action) => {
      state.industriesError = action.error;
      state.industriesLoading = 'idle';
    });
    /*
     * fetchUnions
     */
    builder.addCase(fetchUnions.pending, (state) => {
      state.unions = null;
      state.unionsError = null;
      state.unionsLoading = 'pending';
    });
    builder.addCase(fetchUnions.fulfilled, (state, { payload }) => {
      state.unions = payload;
      state.unionsLoading = 'idle';
    });
    builder.addCase(fetchUnions.rejected, (state, action) => {
      state.unionsError = action.error;
      state.unionsLoading = 'idle';
    });
    /*
     * fetchSchools
     */
    builder.addCase(fetchSchools.pending, (state) => {
      state.schools = null;
      state.schoolsError = null;
      state.schoolsLoading = 'pending';
    });
    builder.addCase(fetchSchools.fulfilled, (state, { payload }) => {
      state.schools = payload;
      state.schoolsLoading = 'idle';
    });
    builder.addCase(fetchSchools.rejected, (state, action) => {
      state.schoolsError = action.error;
      state.schoolsLoading = 'idle';
    });
    /*
     * fetchEducationIndustries
     */
    builder.addCase(fetchEducationIndustries.pending, (state) => {
      state.educationIndustries = null;
      state.educationIndustriesError = null;
      state.educationIndustriesLoading = 'pending';
    });
    builder.addCase(fetchEducationIndustries.fulfilled, (state, { payload }) => {
      state.educationIndustries = payload;
      state.educationIndustriesLoading = 'idle';
    });
    builder.addCase(fetchEducationIndustries.rejected, (state, action) => {
      state.educationIndustriesError = action.error;
      state.educationIndustriesLoading = 'idle';
    });
    /*
     * fetchEducationOthers
     */
    builder.addCase(fetchEducationOthers.pending, (state) => {
      state.educationOthers = null;
      state.educationOthersError = null;
      state.educationOthersLoading = 'pending';
    });
    builder.addCase(fetchEducationOthers.fulfilled, (state, { payload }) => {
      state.educationOthers = payload;
      state.educationOthersLoading = 'idle';
    });
    builder.addCase(fetchEducationOthers.rejected, (state, action) => {
      state.educationOthersError = action.error;
      state.educationOthersLoading = 'idle';
    });
    /*
     * fetchJobs
     */
    builder.addCase(fetchJobs.pending, (state) => {
      state.jobs = null;
      state.jobsError = null;
      state.jobsLoading = 'pending';
    });
    builder.addCase(fetchJobs.fulfilled, (state, { payload }) => {
      state.jobs = payload;
      state.jobsLoading = 'idle';
    });
    builder.addCase(fetchJobs.rejected, (state, action) => {
      state.jobsError = action.error;
      state.jobsLoading = 'idle';
    });
  },
});

// export const { } = slice.actions

export default slice.reducer;
